// Advanced Bus Routes view — a transit/metro-line visualization of every route.
// Shared body rendered inside either shell (desktop master-detail / mobile stacked).
// Centerpiece: each route drawn as a vertical metro line (origin → stops → campus),
// colour-coded per bus, with a "find your stop" search that filters the fleet.

import { useEffect, useMemo, useState } from 'react';
import {
  Bus, MapPin, Search, X, GraduationCap, ArrowRight, Navigation, ChevronRight,
} from 'lucide-react';
import { useBuses, useStoreActions } from '@/store/useStore';
import { T, MonoLabel, EmptyState } from '@/components/dashboard/DashboardShell';
import type { BusRoute } from '@/types';

type ShellComponent = React.ComponentType<{
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  loading?: boolean;
  children: React.ReactNode;
}>;

const CAMPUS = 'Institute of Management Sciences';

// Per-bus accent palette — gives every route its own line colour (metro-map feel).
const PALETTE = ['#14B8A6', '#60A5FA', '#F59E0B', '#A78BFA', '#F472B6', '#34D399'];
const busAccent = (i: number) => PALETTE[i % PALETTE.length];

export function BusRoutesContent({ Shell, mode }: { Shell: ShellComponent; mode: 'desktop' | 'mobile' }) {
  const buses = useBuses();
  const { initializeData } = useStoreActions();
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedReg, setSelectedReg] = useState<string | null>(null);

  useEffect(() => {
    (async () => { await initializeData(); setIsLoading(false); })();
  }, [initializeData]);

  const q = query.trim().toLowerCase();

  // Index each bus with its palette accent so colours stay stable when filtering.
  const indexed = useMemo(
    () => buses.map((b, i) => ({ bus: b, accent: busAccent(i) })),
    [buses],
  );

  const filtered = useMemo(() => {
    if (!q) return indexed;
    return indexed.filter(({ bus }) =>
      bus.bus_number.toLowerCase().includes(q) ||
      bus.registration_number.toLowerCase().includes(q) ||
      bus.routes.some(r => r.toLowerCase().includes(q)),
    );
  }, [indexed, q]);

  const pool = filtered.length ? filtered : indexed;
  const selected = pool.find(x => x.bus.registration_number === selectedReg) ?? pool[0] ?? null;

  // Network stats
  const fleet = buses.length;
  const totalStops = buses.reduce((s, b) => s + b.routes.length, 0);
  const areas = new Set(buses.flatMap(b => b.routes.map(r => r.toLowerCase()))).size;

  const badge = fleet > 0 ? (
    <span style={{
      fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', color: T.teal,
      background: T.tealBg, border: `1px solid ${T.tealBorder}`, borderRadius: 4,
      padding: mode === 'desktop' ? '3px 9px' : '2px 7px',
    }}>{mode === 'desktop' ? `${fleet} BUSES` : fleet}</span>
  ) : undefined;

  if (buses.length === 0 && !isLoading) {
    return (
      <Shell title="Bus Routes" subtitle="University transport network">
        <EmptyState icon={Bus} text="NO BUS ROUTES AVAILABLE" />
      </Shell>
    );
  }

  return (
    <Shell
      title="Bus Routes"
      subtitle={mode === 'desktop' ? 'University transport network' : 'Transport network'}
      loading={isLoading}
      badge={badge}
    >
      <style>{`
        @keyframes busPulse { 0%,100% { transform: scale(1); opacity: 1 } 50% { transform: scale(1.18); opacity: .75 } }
        @keyframes busDash  { to { stroke-dashoffset: -12 } }
        .busStopRow:hover .busStopName { color: #fff !important; }
      `}</style>

      {/* ── Hero / network summary ───────────────────────────────────────── */}
      <NetworkHero fleet={fleet} totalStops={totalStops} areas={areas} compact={mode === 'mobile'} />

      {/* ── Search ───────────────────────────────────────────────────────── */}
      <SearchBar
        query={query}
        setQuery={setQuery}
        matches={q ? filtered.length : null}
      />

      {/* ── Master / detail ──────────────────────────────────────────────── */}
      {mode === 'desktop' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '296px 1fr', gap: 16, alignItems: 'start' }}>
          {/* Bus list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, position: 'sticky', top: 8 }}>
            <MonoLabel size={8}>{q ? `${filtered.length} MATCHING` : 'SELECT A ROUTE'}</MonoLabel>
            {pool.map(({ bus, accent }) => (
              <BusListCard
                key={bus.registration_number}
                bus={bus}
                accent={accent}
                active={selected?.bus.registration_number === bus.registration_number}
                onClick={() => setSelectedReg(bus.registration_number)}
              />
            ))}
            {pool.length === 0 && <EmptyState icon={Search} text="NO ROUTES MATCH" />}
          </div>

          {/* Route detail */}
          {selected && <RouteDetail bus={selected.bus} accent={selected.accent} query={q} />}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Bus selector pills */}
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, margin: '-2px 0' }}>
            {pool.map(({ bus, accent }) => {
              const active = selected?.bus.registration_number === bus.registration_number;
              const num = bus.bus_number.replace(/[^0-9]/g, '') || bus.bus_number;
              return (
                <button
                  key={bus.registration_number}
                  onClick={() => setSelectedReg(bus.registration_number)}
                  style={{
                    flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
                    padding: '9px 14px', borderRadius: 10, cursor: 'pointer',
                    background: active ? `${accent}1A` : T.surface,
                    border: `1px solid ${active ? accent : T.border}`,
                    transition: '.15s',
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: accent, flexShrink: 0 }} />
                  <span style={{ fontFamily: T.mono, fontSize: 11, letterSpacing: '.06em', color: active ? '#fff' : T.text50 }}>
                    {num}
                  </span>
                </button>
              );
            })}
          </div>
          {pool.length === 0 && <EmptyState icon={Search} text="NO ROUTES MATCH" />}
          {selected && <RouteDetail bus={selected.bus} accent={selected.accent} query={q} />}
        </div>
      )}
    </Shell>
  );
}

// ─── Network hero ───────────────────────────────────────────────────────────────
function NetworkHero({ fleet, totalStops, areas, compact }: { fleet: number; totalStops: number; areas: number; compact: boolean }) {
  const stats = [
    { num: fleet,      lbl: 'FLEET' },
    { num: totalStops, lbl: 'STOPS' },
    { num: areas,      lbl: 'AREAS' },
  ];
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(20,184,166,.12) 0%, rgba(6,182,212,.05) 100%)',
      border: '1px solid rgba(20,184,166,.16)',
      borderRadius: 14, padding: compact ? '18px 18px' : '22px 24px',
      position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 18, flexWrap: 'wrap',
    }}>
      <div style={{ position: 'absolute', top: -50, right: -30, width: 180, height: 180, background: 'radial-gradient(circle, rgba(20,184,166,.18) 0%, transparent 65%)', pointerEvents: 'none' }} />
      <Bus
        size={compact ? 84 : 120}
        style={{ position: 'absolute', right: -8, bottom: -24, color: 'rgba(20,184,166,.06)', pointerEvents: 'none' }}
        aria-hidden
      />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <MonoLabel size={8} color="rgba(20,184,166,.65)">UNIVERSITY TRANSPORT</MonoLabel>
        <div style={{
          fontFamily: T.serif, fontSize: compact ? 24 : 30, fontWeight: 400,
          letterSpacing: '-.02em', lineHeight: 1.05, marginTop: 6, maxWidth: 360,
        }}>
          Find your route home
        </div>
        <div style={{ fontSize: compact ? 11 : 12, color: T.text50, fontWeight: 300, marginTop: 6, maxWidth: 380 }}>
          {fleet} buses connecting {areas} neighbourhoods to campus.
        </div>
      </div>

      <div style={{ display: 'flex', position: 'relative', zIndex: 1 }}>
        {stats.map(({ num, lbl }, i) => (
          <div key={lbl} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ textAlign: 'center', padding: '0 16px' }}>
              <div style={{ fontFamily: T.serif, fontSize: compact ? 24 : 30, color: T.teal, lineHeight: 1 }}>{num}</div>
              <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', color: T.text25, marginTop: 4 }}>{lbl}</div>
            </div>
            {i < stats.length - 1 && <div style={{ width: 1, height: 34, background: 'rgba(255,255,255,.08)' }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Search bar ─────────────────────────────────────────────────────────────────
function SearchBar({ query, setQuery, matches }: { query: string; setQuery: (s: string) => void; matches: number | null }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: T.surface,
      border: `1px solid ${focus ? T.tealBorder : T.border}`,
      borderRadius: 12, padding: '0 14px',
      boxShadow: focus ? '0 0 0 3px rgba(20,184,166,.08)' : 'none',
      transition: 'border-color .2s',
    }}>
      <Search size={15} color={focus ? T.teal : T.text35} style={{ flexShrink: 0 }} />
      <input
        placeholder="Find your stop or area — e.g. Hayatabad, Ring Road…"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: T.text, fontFamily: T.sans, fontSize: 13, height: 46 }}
      />
      {matches !== null && (
        <span style={{
          fontFamily: T.mono, fontSize: 9, color: T.teal,
          background: T.tealBg, border: `1px solid ${T.tealBorder}`, borderRadius: 4, padding: '2px 7px',
        }}>{matches}</span>
      )}
      {query && (
        <button onClick={() => setQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.text35, display: 'flex', alignItems: 'center', padding: 0 }}>
          <X size={14} />
        </button>
      )}
    </div>
  );
}

// ─── Bus list card (desktop master) — modelled on the home page cards ─────────────
function BusListCard({ bus, accent, active, onClick }: { bus: BusRoute; accent: string; active: boolean; onClick: () => void }) {
  const [hov, setHov] = useState(false);
  const origin = bus.routes[0];
  const last = bus.routes[bus.routes.length - 1];
  const lit = active || hov;
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        textAlign: 'left', cursor: 'pointer', width: '100%',
        display: 'flex', alignItems: 'center', gap: 11,
        background: active ? `${accent}12` : hov ? 'rgba(20,184,166,.05)' : 'rgba(255,255,255,.03)',
        border: `1px solid ${active ? `${accent}55` : hov ? 'rgba(255,255,255,.1)' : 'rgba(255,255,255,.06)'}`,
        borderLeft: `2px solid ${accent}`,
        borderRadius: 10, padding: '11px 13px', transition: '.2s',
      }}
    >
      {/* Icon box (home Quick-Access style — scales on hover) */}
      <div style={{
        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
        background: `${accent}1A`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent,
        transform: hov ? 'scale(1.08)' : 'scale(1)', transition: 'transform .2s',
      }}>
        <Bus size={14} aria-hidden />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.2, color: lit ? '#fff' : 'rgba(255,255,255,.7)' }}>{bus.bus_number}</span>
          <span style={{ fontFamily: T.mono, fontSize: 8.5, letterSpacing: '.04em', color: accent, whiteSpace: 'nowrap', flexShrink: 0 }}>{bus.routes.length} STOPS</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'rgba(255,255,255,.3)', fontWeight: 300, marginTop: 3, minWidth: 0 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{origin}</span>
          <ArrowRight size={9} style={{ flexShrink: 0, color: 'rgba(255,255,255,.2)' }} aria-hidden />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{last}</span>
        </div>
      </div>

      <ChevronRight size={13} style={{ flexShrink: 0, color: lit ? accent : 'rgba(255,255,255,.15)', transition: 'color .2s' }} aria-hidden />
    </button>
  );
}

// ─── Route detail (metro line) ───────────────────────────────────────────────────
function RouteDetail({ bus, accent, query }: { bus: BusRoute; accent: string; query: string }) {
  const stops = bus.routes;
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: 16, overflow: 'hidden',
    }}>
      {/* Detail header */}
      <div style={{
        padding: '18px 22px', borderBottom: `1px solid ${T.border}`,
        background: `linear-gradient(180deg, ${accent}0E 0%, transparent 100%)`,
        position: 'relative',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 11, flexShrink: 0,
              background: `${accent}1A`, border: `1px solid ${accent}44`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent,
            }}>
              <Bus size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ fontFamily: T.serif, fontSize: 24, fontWeight: 400, letterSpacing: '-.01em', margin: 0, lineHeight: 1.1 }}>{bus.bus_number}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.06em', color: accent, background: `${accent}14`, border: `1px solid ${accent}33`, borderRadius: 4, padding: '2px 7px' }}>{bus.registration_number}</span>
                <MonoLabel size={8}>{stops.length} STOPS</MonoLabel>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: T.mono, fontSize: 9, letterSpacing: '.08em', color: T.text35 }}>
            <MapPin size={11} color={accent} /> {stops[0]}
            <ArrowRight size={11} color={T.text25} />
            <GraduationCap size={12} color={accent} /> CAMPUS
          </div>
        </div>
      </div>

      {/* Metro line */}
      <div style={{ padding: '22px 22px 26px', position: 'relative' }}>
        {/* Spine */}
        <div style={{
          position: 'absolute', left: 36, top: 34, bottom: 36, width: 2,
          background: `linear-gradient(${accent} 0%, ${accent}66 100%)`,
        }} />

        {stops.map((stop, i) => {
          const matched = !!query && stop.toLowerCase().includes(query);
          const isOrigin = i === 0;
          return (
            <StopRow
              key={i}
              label={stop}
              index={i}
              accent={accent}
              kind={isOrigin ? 'origin' : 'stop'}
              matched={matched}
            />
          );
        })}

        {/* Campus destination */}
        <StopRow label={CAMPUS} index={stops.length} accent={accent} kind="campus" matched={false} />
      </div>
    </div>
  );
}

function StopRow({ label, index, accent, kind, matched }: {
  label: string; index: number; accent: string; kind: 'origin' | 'stop' | 'campus'; matched: boolean;
}) {
  const node =
    kind === 'campus' ? (
      <div style={{
        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
        background: accent, border: `2px solid ${T.bg}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 0 0 4px ${accent}22, 0 0 14px ${accent}66`,
      }}>
        <GraduationCap size={15} color="#06231f" />
      </div>
    ) : kind === 'origin' ? (
      <div style={{
        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
        background: accent, border: `2px solid ${T.bg}`,
        boxShadow: `0 0 0 4px ${accent}22, 0 0 10px ${accent}55`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'busPulse 2.4s ease-in-out infinite',
      }}>
        <Navigation size={11} color="#06231f" />
      </div>
    ) : (
      <div style={{
        width: matched ? 18 : 13, height: matched ? 18 : 13, borderRadius: '50%', flexShrink: 0,
        background: matched ? accent : T.bg,
        border: `2px solid ${matched ? accent : `${accent}88`}`,
        boxShadow: matched ? `0 0 0 4px ${accent}22` : 'none',
        transition: '.15s',
      }} />
    );

  const meta =
    kind === 'campus' ? 'DESTINATION · CAMPUS'
    : kind === 'origin' ? 'ORIGIN'
    : `STOP ${String(index).padStart(2, '0')}`;

  return (
    <div className="busStopRow" style={{
      display: 'flex', alignItems: 'center', gap: 16,
      minHeight: kind === 'stop' ? 40 : 46, position: 'relative', zIndex: 1,
    }}>
      {/* node column (28px wide, dot centered ~14 to sit on spine at left:35-22=... ) */}
      <div style={{ width: 30, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
        {node}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="busStopName" style={{
          fontSize: kind === 'campus' ? 15 : 13.5,
          fontFamily: kind === 'campus' ? T.serif : T.sans,
          fontWeight: kind === 'campus' ? 400 : matched ? 600 : 400,
          color: kind === 'campus' || matched ? '#fff' : T.text50,
          lineHeight: 1.25, letterSpacing: kind === 'campus' ? '-.01em' : 0,
          transition: 'color .15s',
        }}>
          {label}
        </div>
        <div style={{
          fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', marginTop: 2,
          color: matched ? accent : kind === 'origin' || kind === 'campus' ? `${accent}99` : T.text25,
        }}>
          {meta}{matched ? ' · MATCH' : ''}
        </div>
      </div>
    </div>
  );
}
